import classNames from 'classnames'
import { AnchorHTMLAttributes, ButtonHTMLAttributes, ComponentType, ReactNode } from 'react'

import type { IconProps } from '../icon/icon.js'
import './button.scss'

export type ButtonSize = 'small' | 'medium' | 'header'
export type ButtonVariant =
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'alert'
    | 'success'
    | 'arrow'
    | 'link'

export interface ButtonProps extends CommonButtonProps {
    onClick?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void
    anchorProps?: Partial<AnchorHTMLAttributes<HTMLAnchorElement>> & { disabled?: boolean }
    buttonProps?: Partial<ButtonHTMLAttributes<HTMLButtonElement>>
    href?: string
}
export interface CommonButtonProps {
    className?: string
    iconLeft?: ComponentType<IconProps>
    iconRight?: ComponentType<IconProps>
    iconOnly?: boolean
    iconLeftProps?: Partial<IconProps>
    iconRightProps?: Partial<IconProps>
    text: string
    isDisabled?: boolean
    size?: ButtonSize
    variant?: ButtonVariant
    inverted?: boolean
}

export const Button = (props: ButtonProps) => {
    const handleClick = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        if (!props.isDisabled) props.onClick?.(e)
    }

    const commonProps = {
        className: getButtonClass(props),
        onClick: handleClick,
        'aria-disabled': props.isDisabled,
    }

    if (props.href) {
        return (
            <a
                {...commonProps}
                href={props.isDisabled ? undefined : props.href}
                {...props.anchorProps}
            >
                {renderIconLeft(props)}
                <span className="button__text">{props.text}</span>
                {renderIconRight(props)}
            </a>
        )
    }

    return (
        <button {...commonProps} disabled={props.isDisabled} type="button" {...props.buttonProps}>
            {renderIconLeft(props)}
            <span className="button__text">{props.text}</span>
            {renderIconRight(props)}
        </button>
    )
}

export const getButtonClass = (props: CommonButtonProps): string => {
    const isIconOnly = props.iconOnly
    return classNames(
        'button',
        props.size && `button--size-${props.size}`,
        props.variant && `button--variant-${props.variant}`,
        !props.variant && 'button--variant-primary',
        props.isDisabled && 'button--disabled',
        isIconOnly && 'button--icon-only',
        props.variant === 'link' && props.inverted && 'button--inverted',
        props.className,
    )
}

const getIconClass = (props: CommonButtonProps): string => {
    const isLinkVariant = props.variant === 'link'
    return classNames(
        'button__icon',
        props.isDisabled && 'button__icon--disabled',
        isLinkVariant && props.inverted && 'button__icon--inverted',
    )
}

export const renderIconLeft = (props: ButtonProps): ReactNode | false => {
    if (!props.iconLeft) return false
    return <props.iconLeft className={getIconClass(props)} {...props.iconLeftProps} />
}

export const renderIconRight = (props: ButtonProps): ReactNode | false => {
    if (!props.iconRight) return false
    return <props.iconRight className={getIconClass(props)} {...props.iconRightProps} />
}
